import { Check, Chrome, Hammer, MessageCircle, MoveLeft, PenLine } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ResponsiveDialog } from 'ui';
import { isInIframe } from 'utils/proxyUtils';
import { getSelectedSiteCollaborateInfo } from '../../../store/siteInfoSlice';
import { getHelpModalOpen, setHelpModalOpen } from '../../../store/uiSlice';

interface Props {
  siteId: string
}
export const HelpModal = ({ siteId }: Props) => {
  const open = useSelector(getHelpModalOpen);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const siteCollaborateInfo = useSelector(getSelectedSiteCollaborateInfo);
  const toggleCompleteTasksModal = (open: boolean) => {
    dispatch(setHelpModalOpen(open));
  };
  const hasPermission = useMemo(() => {
    return siteCollaborateInfo?.result?.role === 'admin' || siteCollaborateInfo?.result?.role === 'contributor';
  }, [siteCollaborateInfo?.result?.role]);

  const navigate = (url: string) => {
    if (isInIframe()) {
      // Send message to parent for navigation
      window.parent.postMessage({
        type: 'NAVIGATE',
        url
      }, '*'); // Will likely replace '*' with production/test origins
    } else {
      // Direct navigation when used as a module/script
      window.open(`{import.meta.env.VITE_APP_ATARIM_URL}${url}`, '_blank');
    }
  };

  const handleNavigationClick = (link: string) => {
    navigate(link);
  };
  return (
    <ResponsiveDialog open={open} onOpenChange={toggleCompleteTasksModal}>
      <ResponsiveDialog.Content className="max-w-3xl tall:max-h-[95vh] mh700:max-h-[100vh] mh700:h-full tall:h-min" container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}>
        <ResponsiveDialog.Header className='pb-0'>
          <ResponsiveDialog.Title className="flex items-center gap-2 mt-2">
            {t('help_modal.title', 'Show us exactly what you want')}
          </ResponsiveDialog.Title>
          <ResponsiveDialog.Description className='pb-4'>
            {t('help_modal.description', 'Our new tools allows you to leave comments directly on your site, here\'s how it works.')}
          </ResponsiveDialog.Description>
        </ResponsiveDialog.Header>
        <div className='flex flex-col grow overflow-auto max-h-[75vh] h-full'>
          <iframe
            src={
              siteCollaborateInfo?.result?.setting?.whitelabel.tutorial_video ||
              'https://player.vimeo.com/video/1094330093?h=710b4ed2e4'
            }
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            encrypted-media
            title="Help Video"
            className='aspect-video w-full rounded-md'
          ></iframe>
          {siteCollaborateInfo?.result?.setting?.whitelabel.mention_atarim === false && hasPermission && (
            <div className='flex flex-col w-full mt-4'>
              <div className='block text-lg font-semibold mb-2 text-textcolor'>
                {t('help_modal.other_resources', 'Other Resources')}
              </div>
              <div className='grid grid-cols-2 text-textcolor mb-4'>
                <a href="https://atarim.io/help/core-features/visual-collaboration-tools-overview/" target="_blank" className='p-2 flex gap-2 cursor-pointer hover:text-tertiary'>
                  <Hammer size={18} className='mt-1 h-6 w-10' />
                  <div className='flex flex-col justify-start gap-1'>
                    <div className='font-medium text-md flex '>{t('help_modal.other_resources_section_1_heading', 'Visual Collaboration Tools Overview')}</div>
                    <p className='text-sm'>{t('help_modal.other_resources_section_1_body', 'Discover how Atarim’s visual collaboration tools simplify feedback and accelerate project workflows.')}</p>
                  </div>
                </a>
                <a href="https://atarim.io/help/core-features/how-to-use-the-annotation-tools-for-feedback-in-atarim/" target="_blank" className='p-2 flex gap-2 cursor-pointer hover:text-tertiary'>
                  <PenLine size={18} className='mt-1 h-6 w-10' />
                  <div className='flex flex-col justify-start gap-1'>
                    <div className='font-medium text-md flex'>{t('help_modal.other_resources_section_2_heading', 'Introduction to Annotation Tools')}</div>
                    <p className='text-sm'>{t('help_modal.other_resources_section_2_body', 'Discover how Atarim’s visual collaboration tools simplify feedback and accelerate project workflows.')} </p>
                  </div>
                </a>
                <a href="https://atarim.io/help/getting-started/creating-tasks/" target="_blank" className='p-2 flex gap-2 cursor-pointer hover:text-tertiary'>
                  <MessageCircle size={18} className='mt-1 h-6 w-10' />
                  <div className='flex flex-col justify-start gap-1'>
                    <div className='font-medium text-md flex'>{t('help_modal.other_resources_section_3_heading', 'How To Create Tasks')}</div>
                    <p className='text-sm'>{t('help_modal.other_resources_section_3_body', 'Discover the fundamentals of task creation and explore the functions of every feature in the popover.')}</p>
                  </div>
                </a>
                <a href="https://atarim.io/help/getting-started/install-chrome-extension/" target="_blank" className='p-2 flex gap-2 cursor-pointer hover:text-tertiary'>
                  <Chrome size={18} className='mt-1 h-6 w-10' />
                  <div className='flex flex-col justify-start gap-1'>
                    <div className='font-medium text-md flex'>{t('help_modal.other_resources_section_4_heading', 'Install the Chrome Plugin')}</div>
                    <p className='text-sm'>{t('help_modal.other_resources_section_4_body', 'Installing the Atarim Chrome plugin is perfect for you if you want to work on sites behind a login screen')}. </p>
                  </div>
                </a>
              </div>
            </div>
          )}
        </div>

        <ResponsiveDialog.Footer className='flex justify-between w-full px-0 pb-2 grow'>

          {hasPermission && (
            <Button size="sm"
              onClick={() => handleNavigationClick('/')}
              variant="outline"
              leftIcon={<MoveLeft size={18} />}>
              {t('help_modal_button_dashboard', 'Return to Dashboard')}
            </Button>
          )}
          <ResponsiveDialog.Close asChild>
            <Button
              size="sm"
              rightIcon={<Check size={18} />}>
              {t('help_modal_button_close', 'Got it')}
            </Button>
          </ResponsiveDialog.Close>
        </ResponsiveDialog.Footer>


      </ResponsiveDialog.Content>
    </ResponsiveDialog>
  );
};
